import React, { useState } from 'react';
import { 
  DollarSign, ExternalLink, Copy, CheckCircle, 
  Zap, BarChart3, Globe, Shield, Code, Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const AdNetworkSetup = () => {
  const [selectedNetwork, setSelectedNetwork] = useState('trafficjunky');

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!');
  };

  const adNetworks = {
    trafficjunky: {
      name: 'TrafficJunky',
      description: 'Premium adult ad network, official partner of Pornhub/MindGeek',
      url: 'https://www.trafficjunky.com/',
      pros: ['High CPM rates', 'Adult-friendly', 'Large inventory', 'Good support'],
      cons: ['Strict approval process', 'Min $100 payout'],
      avgCpm: '$0.50 - $5.00',
      setupSteps: [
        'Sign up at trafficjunky.com as a Publisher',
        'Submit your website for approval',
        'Once approved, create ad zones for each placement',
        'Get your ad zone codes',
        'Replace placeholder ads with TrafficJunky code'
      ]
    },
    exoclick: {
      name: 'ExoClick',
      description: 'One of the largest adult ad networks with global reach',
      url: 'https://www.exoclick.com/',
      pros: ['Easy approval', 'Multiple ad formats', 'Good fill rates', 'Real-time stats'],
      cons: ['Lower CPMs in some regions', 'Some low-quality ads'],
      avgCpm: '$0.20 - $3.00',
      setupSteps: [
        'Register at exoclick.com as a Publisher',
        'Add your website and verify ownership',
        'Create ad zones (banner, native, popunder)',
        'Copy the embed code for each zone',
        'Integrate codes into your website'
      ]
    },
    juicyads: {
      name: 'JuicyAds',
      description: 'Established adult ad network since 2006',
      url: 'https://www.juicyads.com/',
      pros: ['Reliable payments', 'Adult-focused', 'Good banner options', 'Weekly payments'],
      cons: ['Basic interface', 'Limited formats'],
      avgCpm: '$0.30 - $2.50',
      setupSteps: [
        'Sign up at juicyads.com',
        'Submit website for review',
        'Create ad spots for different sizes',
        'Get your JuicyAds script tags',
        'Add scripts to your ad components'
      ]
    }
  };

  const adPlacements = [
    { 
      name: 'Leaderboard (728x90)', 
      location: 'Top of page / Between sections',
      file: '/app/frontend/src/components/AdBanner.jsx',
      component: 'LeaderboardAd',
      revenue: 'Medium-High'
    },
    { 
      name: 'Sidebar (300x250)', 
      location: 'Right sidebar',
      file: '/app/frontend/src/components/AdBanner.jsx',
      component: 'SidebarAd',
      revenue: 'Medium'
    },
    { 
      name: 'Sticky Footer (970x90)', 
      location: 'Bottom of viewport',
      file: '/app/frontend/src/components/AdBanner.jsx',
      component: 'StickyFooterAd',
      revenue: 'High'
    },
    { 
      name: 'In-Feed Native', 
      location: 'Between video cards',
      file: '/app/frontend/src/components/AdBanner.jsx',
      component: 'InFeedAd',
      revenue: 'Medium-High'
    },
    { 
      name: 'Video Pre-roll', 
      location: 'Before video plays',
      file: '/app/frontend/src/components/AdBanner.jsx',
      component: 'PrerollAd',
      revenue: 'Very High'
    }
  ];

  const integrationCode = `// Example: Replace placeholder in AdBanner.jsx

// Current placeholder:
<div className="ad-placeholder">Ad Space</div>

// Replace with TrafficJunky code:
<div dangerouslySetInnerHTML={{ __html: \`
  <script type="text/javascript">
    var ad_idzone = "YOUR_ZONE_ID";
    var ad_width = "728";
    var ad_height = "90";
  </script>
  <script type="text/javascript" src="https://a.trafficjunky.net/ad?..."></script>
\` }} />

// Or ExoClick:
<iframe 
  src="https://syndication.exoclick.com/ads-iframe/YOUR_ZONE_ID"
  width="728" 
  height="90"
  frameBorder="0"
  scrolling="no"
/>`;

  const network = adNetworks[selectedNetwork];

  return (
    <div className="min-h-screen pt-20 px-4 md:px-8 lg:px-16 py-8 bg-[#050505]" data-testid="ad-setup-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mx-auto mb-6">
            <DollarSign className="w-10 h-10 text-white" />
          </div>
          <h1 
            className="text-4xl md:text-5xl font-normal tracking-wide uppercase mb-4 text-white"
            style={{ fontFamily: 'Bebas Neue, sans-serif' }}
          >
            Monetization Setup Guide
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Follow this guide to connect real ad networks and start earning revenue from your site traffic.
          </p>
        </div>

        {/* Revenue Potential */}
        <div className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border border-green-500/20 rounded-lg p-6 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <BarChart3 className="w-6 h-6 text-green-400" />
            <h2 className="text-xl font-semibold text-white">Estimated Revenue Potential</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-1">10K Daily Views</p>
              <p className="text-2xl font-bold text-green-400">$50 - $200/day</p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-1">50K Daily Views</p>
              <p className="text-2xl font-bold text-green-400">$250 - $1000/day</p>
            </div>
            <div className="bg-black/30 rounded-lg p-4">
              <p className="text-gray-400 text-sm mb-1">100K+ Daily Views</p>
              <p className="text-2xl font-bold text-green-400">$500 - $2500+/day</p>
            </div>
          </div>
        </div>

        {/* Ad Networks */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
            <Globe className="w-6 h-6" />
            Recommended Ad Networks
          </h2>
          
          {/* Network Tabs */}
          <div className="flex flex-wrap gap-3 mb-6">
            {Object.entries(adNetworks).map(([key, net]) => (
              <button
                key={key}
                onClick={() => setSelectedNetwork(key)}
                className={`px-6 py-3 rounded-lg font-medium transition-all ${
                  selectedNetwork === key 
                    ? 'bg-white text-black' 
                    : 'bg-[#1A1A1A] text-gray-400 hover:bg-white/10'
                }`}
              >
                {net.name}
              </button>
            ))}
          </div>

          {/* Selected Network Details */}
          <div className="bg-[#121212] border border-white/10 rounded-lg p-6">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">{network.name}</h3>
                <p className="text-gray-400">{network.description}</p>
              </div>
              <a 
                href={network.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded hover:bg-gray-200 transition-colors"
              >
                Sign Up <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <h4 className="text-green-400 font-medium mb-3 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" /> Pros
                </h4>
                <ul className="space-y-2">
                  {network.pros.map((pro, i) => (
                    <li key={i} className="text-gray-300 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full"></span>
                      {pro}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-amber-400 font-medium mb-3">Considerations</h4>
                <ul className="space-y-2">
                  {network.cons.map((con, i) => (
                    <li key={i} className="text-gray-300 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full"></span>
                      {con}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-black/40 rounded-lg p-4 mb-6">
              <p className="text-gray-400 text-sm mb-1">Average CPM (Cost Per 1000 Impressions)</p>
              <p className="text-xl font-bold text-white">{network.avgCpm}</p>
            </div>

            <div>
              <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                <Settings className="w-4 h-4" /> Setup Steps
              </h4>
              <ol className="space-y-3">
                {network.setupSteps.map((step, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-white/10 flex items-center justify-center text-sm text-gray-400 flex-shrink-0">
                      {i + 1}
                    </span>
                    <span className="text-gray-300">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>

        {/* Your Ad Placements */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
            <Zap className="w-6 h-6" />
            Your Ad Placements
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {adPlacements.map((placement, index) => (
              <div 
                key={index}
                className="bg-[#121212] border border-white/10 rounded-lg p-5"
              >
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-white font-medium">{placement.name}</h3>
                  <span className={`px-2 py-1 text-xs rounded ${
                    placement.revenue === 'Very High' ? 'bg-green-500/20 text-green-400' :
                    placement.revenue === 'High' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>
                    {placement.revenue} Revenue
                  </span>
                </div>
                <p className="text-gray-500 text-sm mb-3">{placement.location}</p>
                <div className="flex items-center justify-between text-xs">
                  <code className="text-gray-400 bg-black/40 px-2 py-1 rounded">
                    {placement.component}
                  </code>
                  <button
                    onClick={() => copyToClipboard(placement.file)}
                    className="text-gray-500 hover:text-white flex items-center gap-1"
                  >
                    <Copy className="w-3 h-3" /> Copy path
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Integration Code Example */}
        <div className="mb-12">
          <h2 className="text-2xl font-semibold text-white mb-6 flex items-center gap-2">
            <Code className="w-6 h-6" />
            Integration Example
          </h2>
          
          <div className="bg-[#0A0A0A] border border-white/10 rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 bg-[#1A1A1A] border-b border-white/10">
              <span className="text-gray-400 text-sm">AdBanner.jsx modification</span>
              <button
                onClick={() => copyToClipboard(integrationCode)}
                className="text-gray-400 hover:text-white flex items-center gap-2 text-sm"
              >
                <Copy className="w-4 h-4" /> Copy
              </button>
            </div>
            <pre className="p-4 text-sm text-gray-300 overflow-x-auto">
              <code>{integrationCode}</code>
            </pre>
          </div>
        </div>

        {/* Important Notes */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-6">
          <h3 className="text-amber-400 font-semibold mb-4 flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Important Notes
          </h3>
          <ul className="space-y-2 text-gray-300">
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2"></span>
              <span>Google AdSense does NOT allow adult content - use adult-specific networks only</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2"></span>
              <span>Most networks require your site to have some traffic before approval (500+ daily visitors)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2"></span>
              <span>Premium users should see NO ads - this is handled in the AdBanner component</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="w-1.5 h-1.5 bg-amber-500 rounded-full mt-2"></span>
              <span>Test thoroughly after integration - broken ads hurt user experience and revenue</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AdNetworkSetup;
